import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, RefreshCw, Eye, Trash2 } from "lucide-react";

export default function AdminContactsTab({ apiUrl, getAuthHeaders }) {
  const credFetch = (url, opts = {}) => fetch(url, { ...opts, credentials: "include" });

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadContacts(); }, []);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const res = await credFetch(`${apiUrl}/api/admin/contacts`, { headers: getAuthHeaders(), credentials: "include" });
      const data = await res.json();
      if (data.success) setContacts(data.contacts);
    } catch (error) { console.error("Contacts error:", error); }
    setLoading(false);
  };

  const updateContactStatus = async (id, status) => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/contacts/${id}/status?status=${status}`, { method: "PUT", headers: getAuthHeaders(), credentials: "include", credentials: "include" });
      if (res.ok) { toast.success("Status updated"); loadContacts(); }
    } catch (error) { toast.error("Update failed"); }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      const res = await credFetch(`${apiUrl}/api/admin/contacts/${id}`, { method: "DELETE", headers: getAuthHeaders(), credentials: "include", credentials: "include" });
      if (res.ok) { toast.success("Deleted"); loadContacts(); }
    } catch (error) { toast.error("Delete failed"); }
  };

  const exportData = async () => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/export/contacts`, { headers: getAuthHeaders(), credentials: "include" });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "contacts_export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Contacts exported");
    } catch (error) { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-contacts-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Contact Messages</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportData}><Download className="w-4 h-4 mr-2" />Export CSV</Button>
          <Button onClick={loadContacts} variant="outline"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
        </div>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell className="font-medium">{contact.name}</TableCell>
                <TableCell>{contact.email}</TableCell>
                <TableCell className="max-w-xs truncate">{contact.subject}</TableCell>
                <TableCell>
                  <Select value={contact.status} onValueChange={(value) => updateContactStatus(contact.id, value)}>
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unread">Unread</SelectItem>
                      <SelectItem value="read">Read</SelectItem>
                      <SelectItem value="replied">Replied</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-sm text-gray-500">{new Date(contact.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="ghost"><Eye className="w-4 h-4" /></Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Message from {contact.name}</DialogTitle></DialogHeader>
                        <div className="space-y-4">
                          <div><p className="text-sm text-gray-500">Email</p><p>{contact.email}</p></div>
                          {contact.phone && <div><p className="text-sm text-gray-500">Phone</p><p>{contact.phone}</p></div>}
                          <div><p className="text-sm text-gray-500">Subject</p><p>{contact.subject}</p></div>
                          <div><p className="text-sm text-gray-500">Message</p><p className="whitespace-pre-wrap">{contact.message}</p></div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteContact(contact.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
